/* JV Product additions — no dependencies, no external location provider. */
(() => {
  'use strict';
  const iso = value => /^[A-Z]{2}$/.test(String(value || '').toUpperCase()) ? String(value).toUpperCase() : '';
  const timestamp = value => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/.test(value || '')) return NaN;
    if (!day(value.slice(0, 10)) || Number(value.slice(11, 13)) > 23 || Number(value.slice(14, 16)) > 59 || Number(value.slice(17, 19)) > 59) return NaN;
    return Date.parse(value);
  };
  const offerActive = (c, now = Date.now()) => {
    const start = c.offerStart ? timestamp(c.offerStart) : -Infinity;
    const end = c.offerEnd ? timestamp(c.offerEnd) : Infinity;
    return !(c.offerLimited && !c.offerEnd) && start < end && now >= start && now < end;
  };
  const day = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const d = new Date(value + 'T12:00:00Z');
    return Number.isFinite(+d) && d.toISOString().slice(0, 10) === value ? d : null;
  };
  const dateWindow = (c, zone, now = new Date()) => {
    if (!c.fulfillmentConfirmed || !zone?.confirmed || c.estimateMode === 'off') return null;
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {timeZone:c.timeZone, year:'numeric', month:'2-digit', day:'2-digit'}).formatToParts(now);
      const field = n => parts.find(p => p.type === n)?.value;
      const today = day(`${field('year')}-${field('month')}-${field('day')}`);
      const dispatch = c.dispatchDate ? day(c.dispatchDate) : null;
      if (!today || (c.dispatchDate && !dispatch)) return null;
      // Presales stay anchored until dispatch starts. Afterwards new orders roll
      // with the merchant's date; this is not an existing order's tracking ETA.
      const start = dispatch && dispatch > today ? dispatch : today;
      let from, to;
      if (c.estimateMode === 'fixed') { from = day(c.dateFrom); to = day(c.dateTo); }
      else if (['days', 'business'].includes(c.estimateMode) && Number.isInteger(zone.min) && Number.isInteger(zone.max) && zone.min >= 1 && zone.max >= zone.min && zone.max <= 60) {
        const advance = count => {
          const result = new Date(+start);
          for (let added = 0; added < count;) {
            result.setUTCDate(result.getUTCDate() + 1);
            if (c.estimateMode === 'days' || (result.getUTCDay() !== 0 && result.getUTCDay() !== 6)) added++;
          }
          return result;
        };
        from = advance(zone.min); to = advance(zone.max);
      }
      return from && to && from >= start && to >= from ? {from, to} : null;
    } catch { return null; }
  };
  const zoneFor = (c, code) => c.zones?.find(z => z.confirmed && String(z.codes || '').split(',').map(s => iso(s.trim())).filter(Boolean).includes(iso(code)));
  const deliveryPolicy = (c, code, supported) => {
    const excluded = String(c.dutiesExcluded || '').split(',').map(s => iso(s.trim())).filter(Boolean);
    return {free:!!(supported && (c.worldwideFree || zoneFor(c, code)?.free)), duties:!!(supported && c.dutiesIncluded && !excluded.includes(iso(code)))};
  };
  const countryFlag = code => iso(code) ? [...iso(code)].map(c => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('') : '';
  const arrivalCopy = (dates, locale = 'en', now = new Date()) => {
    if (!dates) return 'Delivery information';
    const showYear = dates.from.getUTCFullYear() !== dates.to.getUTCFullYear() || dates.from.getUTCFullYear() !== now.getUTCFullYear();
    const format = new Intl.DateTimeFormat(locale, {month:'short', day:'numeric', ...(showYear ? {year:'numeric'} : {}), timeZone:'UTC'});
    const sameDay = Number(dates.from) === Number(dates.to);
    return `Order now and receive ${sameDay ? 'on' : 'between'} ${format.format(dates.from)}${sameDay ? '' : ' – ' + format.format(dates.to)}`;
  };
  // Only Shopify's documented cart item fields. Never serialize private form/security fields.
  const watchItem = entries => {
    const fields = new Map(entries);
    const id = String(fields.get('id') || '');
    const quantity = Number(fields.get('quantity') || 1);
    if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Please choose a valid watch and quantity.');
    const item = {id, quantity};
    const properties = {};
    for (const [key, value] of fields) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) {
        if (typeof value !== 'string') throw new Error('This bundle cannot be combined with a file upload. Deselect the strap and use the normal product form.');
        Object.defineProperty(properties, match[1], {value, enumerable:true});
      }
    }
    if (Object.keys(properties).length) item.properties = properties;
    if (fields.get('selling_plan')) item.selling_plan = String(fields.get('selling_plan'));
    return item;
  };
  const optionPermitted = (option, quantity) => !!(option && option.dataset.available === 'true' && Number(option.dataset.min) === 1 && Number(option.dataset.increment) === 1 && Number.isSafeInteger(quantity) && quantity > 0 && (!option.dataset.max || Number(option.dataset.max) >= quantity));
  const giftSelection = (label, option) => {
    const title = option?.dataset.title;
    return title && title !== 'Default Title' ? `${label} — ${title}` : label;
  };
  const helpers = {iso, timestamp, offerActive, day, dateWindow, zoneFor, deliveryPolicy, countryFlag, arrivalCopy, watchItem, optionPermitted, giftSelection};
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (typeof window === 'undefined' || customElements.get('jv-product-additions')) return;

  class JVProductAdditions extends HTMLElement {
    connectedCallback() { this.frame = requestAnimationFrame(() => this.mount()); }
    disconnectedCallback() { cancelAnimationFrame(this.frame); this.unmount(); }
    status(message) {
      const note = document.querySelector(`[data-jva-editor="${CSS.escape(this.dataset.sectionId)}"]`);
      if (note) note.textContent = 'JV Product additions: ' + message;
    }
    mount() {
      if (this.hero?.isConnected || !this.isConnected) return;
      this.unmount();
      try { this.config = JSON.parse(this.querySelector('[data-jva-config]').textContent); }
      catch { this.status('Invalid configuration. Existing hero was not changed.'); return; }
      const candidates = [...document.querySelectorAll('jv-product-hero-v2')].filter(h => h.dataset.productId === this.dataset.productId);
      const wrapper = this.closest('.shopify-section');
      const previous = wrapper?.previousElementSibling;
      const hero = previous?.querySelector('jv-product-hero-v2');
      if (candidates.length !== 1 || candidates[0] !== hero || hero.hasAttribute('data-jva-attached')) {
        this.status('Place this section immediately after the single matching JV Product Hero V2. No hero changes applied.'); return;
      }
      const info = hero.querySelector('.jv-fp__info');
      const form = info?.querySelector('form.jv-fp__form');
      const price = info?.querySelector(':scope > .jv-fp__price-line');
      const title = info?.querySelector(':scope > .jv-fp__title');
      if (!info || !form?.getAttribute('id') || !price || !title || !form.querySelector('[name="id"]') || !form.querySelector('[data-jv-fp-atc]')) {
        this.status('This hero version needs its adapter checked. Existing content remains unchanged.'); return;
      }
      this.hero = hero; this.form = form; this.info = info; this.markers = [];
      this.controller = new AbortController();
      this.on = (el, event, fn, options = {}) => el?.addEventListener(event, fn, {...options, signal:this.controller.signal});
      hero.setAttribute('data-jva-attached', this.id);
      const slots = [...this.querySelectorAll('[data-jva-slot]')];
      let top = price;
      // The approved layout is price -> native model row -> strap bundle at
      // every breakpoint. Ignore legacy moveModels values in installed Liquid.
      // Move, never clone: native links, current model and More behavior survive.
      const variations = info.querySelector(':scope > .jv-fp__var-row');
      if (variations) {
        const label = info.querySelector(':scope > .jv-fp__variation-label');
        for (const node of [label, variations].filter(Boolean)) {
          this.move(node, info); top.after(node); top = node;
        }
      }
      this.bundle = slots.find(s => s.dataset.jvaSlot === 'bundle');
      this.below = slots.find(s => s.dataset.jvaSlot === 'below');
      this.delivery = this.below?.querySelector('.jva-delivery');
      if (this.bundle) { this.move(this.bundle, info); top.after(this.bundle); }
      let belowAnchor = form;
      while (belowAnchor.nextElementSibling?.matches('.jv-fp__note')) belowAnchor = belowAnchor.nextElementSibling;
      if (this.below) { this.move(this.below, info); belowAnchor.after(this.below); }
      // Move the original nodes, retaining their editor attributes and the hero's
      // live connection to Product Details. Never clone or hard-code these specs.
      const specNodes = [...info.children].filter(node => node.matches('.jv-fp__specs,[data-jv-spec-link]'));
      if (this.below && specNodes.length) {
        this.specsContainer = document.createElement('div');
        this.specsContainer.className = 'jva-hero-specs';
        this.below.after(this.specsContainer);
        specNodes.forEach(node => this.move(node, this.specsContainer));
      }
      // Purchase Layout may move this original panel outside the below slot.
      this.find = selector => [...slots, this.delivery].filter(Boolean).map(s => s.querySelector(selector)).find(Boolean);
      this.check = this.find('[data-jva-check]');
      this.accessory = this.find('[data-jva-accessory-variant]');
      this.optionsPanel = this.find('[data-jva-options]');
      this.giftProperty = this.find('[data-jva-gift-property]');
      // Shopify's field named "id" shadows form.id in the DOM; always read the attribute.
      if (this.giftProperty) this.giftProperty.setAttribute('form', form.getAttribute('id'));
      this.baseImage = this.find('[data-jva-bundle-image]')?.src;
      this.baseImageAlt = this.find('[data-jva-bundle-image]')?.alt;
      this.dialog = this.find('dialog');
      this.on(this.check, 'change', () => this.updateSelection());
      this.on(this.accessory, 'change', () => this.refreshBundle());
      this.on(window, 'resize', () => this.sizeAccessory());
      this.on(document.fonts, 'loadingdone', () => this.sizeAccessory());
      document.fonts?.ready.then(() => { if (this.hero?.isConnected) this.sizeAccessory(); });
      this.on(form, 'change', event => {
        if (event.target.name === 'id' && this.check) this.check.checked = false;
        this.refresh();
      });
      this.on(form, 'input', () => this.refreshBundle());
      this.on(hero, 'jv:purchase-state', () => this.refresh());
      this.on(form, 'submit', event => this.submit(event), {capture:true});
      this.on(this.find('[data-jva-choose]'), 'click', () => {
        this.bundle?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block:'center'});
        this.check?.focus({preventScroll:true});
      });
      this.on(this.find('[data-jva-size-open]'), 'click', event => {
        if (!this.dialog?.showModal) return;
        this.returnFocus = event.currentTarget; this.oldOverflow = document.documentElement.style.overflow;
        this.dialog.showModal(); document.documentElement.style.overflow = 'hidden';
        this.find('[data-jva-size-close]')?.focus();
      });
      this.on(this.find('[data-jva-size-close]'), 'click', () => this.dialog?.close());
      this.on(this.dialog, 'click', event => {
        const r = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) this.dialog.close();
      });
      this.on(this.dialog, 'close', () => this.closeDialog());
      this.on(window, 'pageshow', () => this.refresh());
      this.on(document, 'visibilitychange', () => { if (!document.hidden) this.refresh(); });
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, 30000);
      this.countrySelect = this.find('[data-jva-country]');
      const countryToggle = this.find('[data-jva-country-toggle]');
      const countryDetails = this.find('[data-jva-country-details]');
      const toggleCountryDetails = open => {
        if (!countryToggle || !countryDetails) return;
        countryDetails.hidden = !open; countryToggle.setAttribute('aria-expanded', String(open));
        if (open) (this.countrySelect || this.find('[data-jva-country-done]'))?.focus({preventScroll:true});
        else { if (this.countrySelect) this.countrySelect.value = this.country.code; countryToggle.focus({preventScroll:true}); }
      };
      this.on(countryToggle, 'click', () => toggleCountryDetails(countryDetails.hidden));
      this.on(this.find('[data-jva-country-done]'), 'click', () => toggleCountryDetails(false));
      this.on(countryDetails, 'keydown', event => { if (event.key === 'Escape') { event.preventDefault(); toggleCountryDetails(false); } });
      this.countries = new Map([...this.countrySelect?.options || []].map(o => [o.value, o.textContent]));
      if (iso(this.config.country?.code)) this.countries.set(this.config.country.code, this.config.country.name);
      this.refresh(); this.loadCountry();
      this.status('Attached to the existing hero. Verify bundle, countries, size guide and cart in this draft.');
    }
    move(node, destination) {
      const marker = document.createComment('JV additions restore point'); node.before(marker);
      this.markers.push({node, marker}); destination.append(node);
    }
    available() { return !!this.form?.querySelector('[data-jv-fp-atc]:not([disabled])'); }
    quantity() { return Number(new FormData(this.form).get('quantity') || 1); }
    selectedOption() { return this.accessory?.selectedOptions[0]; }
    sizeAccessory() {
      if (!this.accessory?.isConnected || this.optionsPanel?.hidden || !this.selectedOption()) return;
      // Native selects otherwise size to the longest option, not the chosen color.
      // Measure with the rendered font, retaining room for the native arrow.
      const style = getComputedStyle(this.accessory);
      const measure = document.createElement('span');
      measure.setAttribute('aria-hidden', 'true');
      measure.textContent = this.selectedOption().textContent.trim();
      Object.assign(measure.style, {position:'absolute', visibility:'hidden', whiteSpace:'pre', font:style.font, letterSpacing:style.letterSpacing, textTransform:style.textTransform});
      this.accessory.parentElement.append(measure);
      const extra = ['paddingLeft','paddingRight','borderLeftWidth','borderRightWidth'].reduce((sum, key) => sum + (parseFloat(style[key]) || 0), 24); // Native arrow + 4px breathing room.
      const width = Math.ceil(measure.getBoundingClientRect().width + extra);
      measure.remove();
      this.accessory.style.setProperty('--jva-option-width', `${Math.max(64, width)}px`);
    }
    optionOK(option, quantity) { return (this.config.mode === 'gift' && !this.config.giftVariants) || optionPermitted(option, quantity); }
    refreshBundle() {
      if (!this.check) return;
      let option = this.selectedOption();
      const qty = this.quantity();
      if (this.accessory) {
        for (const item of this.accessory.options) item.disabled = !optionPermitted(item, qty);
        if (!optionPermitted(option, qty)) {
          // Never retain an invalid checked gift. An eligible default lets the
          // customer opt in again before revealing the otherwise hidden picker.
          this.check.checked = false;
          const replacement = [...this.accessory.options].find(item => !item.disabled);
          if (replacement) { this.accessory.value = replacement.value; option = replacement; }
        }
      }
      const optionOK = this.optionOK(option, qty);
      const permitted = this.config.bundleReady && offerActive(this.config) && this.available() && optionOK && Number.isSafeInteger(qty) && qty > 0;
      if (!this.busy) this.check.disabled = !permitted;
      if (!permitted) this.check.checked = false;
      if (this.bundle) this.bundle.hidden = !offerActive(this.config);
      if (option) {
        if (this.config.mode === 'product') {
          const price = this.find('[data-jva-price]'); if (price) price.textContent = option.dataset.price;
          const compare = this.find('[data-jva-compare]'); if (compare) { compare.textContent = option.dataset.compare; compare.hidden = !option.dataset.compare; }
        } else if (this.config.giftValueSource === 'product') {
          const compare = this.find('[data-jva-gift-compare]');
          const value = this.find('[data-jva-gift-compare-value]');
          if (compare && value) { value.textContent = option.dataset.giftValue || ''; compare.hidden = !option.dataset.giftValue; }
        }
        const image = this.find('[data-jva-bundle-image]');
        if (image && (option.dataset.image || this.baseImage)) {
          image.src = option.dataset.image || this.baseImage;
          image.alt = option.dataset.image ? option.dataset.imageAlt : this.baseImageAlt;
        }
      }
      const note = this.find('[data-jva-bundle-note]');
      if (note) {
        note.textContent = permitted ? (this.config.mode === 'gift' ? '' : 'Optional extra. Adds one strap bundle per watch at the price shown; any Shopify discount is applied in your cart.') : 'This strap option is not available for the selected watch or quantity.';
        note.hidden = !note.textContent;
      }
      this.updateSelection();
    }
    updateSelection() {
      const selected = !!this.check?.checked;
      if (this.optionsPanel) {
        this.optionsPanel.hidden = !selected;
        this.check?.setAttribute('aria-expanded', String(selected));
      }
      if (this.accessory) this.accessory.disabled = !selected || !!this.busy;
      this.hero?.toggleAttribute('data-jva-selected', selected);
      if (this.giftProperty) {
        this.giftProperty.disabled = !(selected && this.config.mode === 'gift');
        this.giftProperty.value = giftSelection(this.config.giftLabel, this.config.giftVariants ? this.selectedOption() : null);
      }
      this.sizeAccessory();
    }
    refresh() {
      if (!this.hero) return;
      this.refreshBundle(); this.renderDelivery();
      const offer = this.find('[data-jva-offer]');
      if (offer) offer.hidden = !(this.config.offerEnabled && this.config.bundleReady && offerActive(this.config));
      const end = this.find('[data-jva-offer-end]');
      if (end) {
        const value = timestamp(this.config.offerEnd); end.hidden = !Number.isFinite(value);
        if (!end.hidden) {
          try { end.textContent = 'Offer ends ' + new Intl.DateTimeFormat(this.config.locale, {dateStyle:'medium', timeStyle:'short', timeZone:this.config.timeZone}).format(value) + ` (${this.config.timeZone}).`; }
          catch { end.textContent = 'Offer ends ' + this.config.offerEnd; }
        }
      }
    }
    loadCountry() { this.setCountry(this.config.country); }
    setCountry(country) {
      const code = iso(country?.code); if (!code) return;
      this.country = {code, name:this.countries.get(code) || String(country.name || code).slice(0, 100)};
      if (this.countrySelect) this.countrySelect.value = code;
      this.renderDelivery();
    }
    renderDelivery() {
      if (!this.country) return;
      const supported = this.countries.has(this.country.code);
      const zone = supported ? zoneFor(this.config, this.country.code) || {confirmed:true,min:this.config.defaultMin,max:this.config.defaultMax} : null;
      const policy = deliveryPolicy(this.config, this.country.code, supported);
      const prefix = this.find('[data-jva-shipping-prefix]');
      if (prefix) prefix.textContent = policy.free ? 'Free Shipping to' : 'Delivering to';
      const countryName = this.find('[data-jva-country-name]'); if (countryName) countryName.textContent = this.country.name;
      const flag = this.find('[data-jva-flag]'); if (flag) flag.textContent = countryFlag(this.country.code);
      const toggle = this.find('[data-jva-country-toggle]');
      if (toggle) toggle.setAttribute('aria-label', `Delivery details for ${this.country.name}${this.countrySelect ? ', change country' : ''}`);
      const duties = this.find('[data-jva-duties]'); if (duties) duties.hidden = !policy.duties;
      const shipping = this.find('[data-jva-shipping]');
      if (shipping) shipping.textContent = !supported ? 'Shipping availability for this destination must be confirmed at checkout.' : `${policy.free ? `Free shipping to ${this.country.name}.` : 'Shipping options are confirmed at checkout.'}${this.config.dutiesIncluded && !policy.duties ? ' Import duties and taxes are not included for this country.' : ''}`;
      const dates = this.available() ? dateWindow(this.config, zone) : null;
      const arrival = this.find('[data-jva-arrival]');
      if (arrival) {
        arrival.hidden = false;
        arrival.textContent = !supported ? 'Delivery availability confirmed at checkout' : arrivalCopy(dates, this.config.locale || 'en');
        arrival.toggleAttribute('data-jva-has-dates', !!dates);
        if (dates) arrival.title = 'Estimated dates. Select the country for delivery details.';
        else arrival.removeAttribute('title');
      }
      const note = this.find('[data-jva-estimate-note]'); if (note) note.hidden = !dates;
    }
    async submit(event) {
      if (this.busy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (!this.check?.checked) return; // Native form and any existing integration are untouched when opted out.
      if (!offerActive(this.config) || !this.available()) {
        event.preventDefault(); event.stopImmediatePropagation(); this.refresh(); this.error('This strap offer is no longer available. Review your selection before adding the watch.'); return;
      }
      if (this.config.mode === 'gift') {
        const quantity = this.quantity();
        if (!this.config.bundleReady || !this.optionOK(this.selectedOption(), quantity) || !Number.isSafeInteger(quantity) || quantity < 1) {
          event.preventDefault(); event.stopImmediatePropagation(); this.refreshBundle(); this.error('Please choose an available strap color and review your selection.'); return;
        }
        this.updateSelection();
        return; // Native Shopify line-item property; deliberately no additional cart item.
      }
      event.preventDefault(); event.stopImmediatePropagation();
      const option = this.selectedOption();
      try {
        const watch = watchItem(new FormData(this.form).entries());
        if (!optionPermitted(option, watch.quantity)) throw new Error('Please choose an available strap option.');
        this.setBusy(true); this.error('');
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify({items:[watch, {id:option.value, quantity:watch.quantity}]})});
        if (!response.ok) throw new Error('Shopify could not confirm the complete bundle. Your cart may have changed. Please review it before trying again.');
        await response.json();
        window.location.assign(root + 'cart');
      } catch (error) {
        this.error(this.busy ? 'The complete bundle could not be confirmed. Your cart may have changed; review it before retrying.' : error.message, this.busy);
        this.setBusy(false);
      }
    }
    error(message, review = false) {
      const error = this.find('[data-jva-error]');
      if (error) { error.textContent = message; error.hidden = !message; }
      const link = this.find('[data-jva-review-cart]'); if (link) link.hidden = !review;
    }
    setBusy(value) {
      this.busy = value;
      this.form?.toggleAttribute('aria-busy', value);
      if (this.check) this.check.disabled = value;
      if (this.accessory) this.accessory.disabled = value;
      if (!value) this.refreshBundle();
    }
    closeDialog() {
      if (this.oldOverflow !== undefined) { document.documentElement.style.overflow = this.oldOverflow; this.oldOverflow = undefined; }
      if (this.returnFocus?.isConnected) this.returnFocus.focus({preventScroll:true});
      this.returnFocus = null;
    }
    unmount() {
      this.controller?.abort(); clearInterval(this.timer);
      if (this.dialog?.open) this.dialog.close(); this.closeDialog();
      this.giftProperty?.removeAttribute('form');
      for (const {node, marker} of [...this.markers || []].reverse()) { if (marker.parentNode) marker.replaceWith(node); }
      this.specsContainer?.remove(); this.specsContainer = null;
      this.hero?.removeAttribute('data-jva-selected'); this.hero?.removeAttribute('data-jva-attached');
      this.form?.removeAttribute('aria-busy');
      this.hero = null; this.form = null; this.delivery = null; this.markers = []; this.busy = false;
    }
  }
  customElements.define('jv-product-additions', JVProductAdditions);
  document.addEventListener('shopify:section:unload', event => {
    for (const addon of document.querySelectorAll('jv-product-additions')) if (event.target.contains(addon) || (addon.hero && event.target.contains(addon.hero))) addon.unmount();
  });
  document.addEventListener('shopify:section:load', () => requestAnimationFrame(() => document.querySelectorAll('jv-product-additions').forEach(addon => addon.mount())));
  document.addEventListener('shopify:section:reorder', () => {
    const addons = [...document.querySelectorAll('jv-product-additions')]; addons.forEach(addon => addon.unmount());
    requestAnimationFrame(() => addons.forEach(addon => addon.mount()));
  });
})();